import React, {Suspense} from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory, useRouteMatch} from 'react-router-dom'

import {Box, CircularProgress, Grid} from '@mui/material'

import MenopauseProgramBlock from '@progyny/pages/Consultations/Menopause/MenopauseProgramBlock'
import {useMenopauseAppointmentTabPresence} from '@progyny/pages/Consultations/Menopause/useMenopauseConsultationAppointments'
import CancelConsultation from '@progyny/pages/Consultations/components/CancelConsultation'
import ConsultationHubNavigationRow from '@progyny/pages/Consultations/components/ConsultationHubNavigationRow'
import ConsultationTypeFilterDropdown, {
  type ConsultationTypeFilter,
} from '@progyny/pages/Consultations/components/ConsultationTypeFilterDropdown'
import OnboardingSection from '@progyny/pages/Consultations/components/OnboardingSection'

const MenopauseAppointmentsLazyPanel = React.lazy(
  () =>
    import('@progyny/pages/Consultations/Menopause/MenopauseAppointmentsLazyPanel')
)

const MENOPAUSE_BASE_PATH = '/consultations/menopause'

export type MenopauseActiveTab = 'overview' | 'upcoming' | 'past'

interface MenopauseViewContentProps {
  consultation: any
  colors: any
  theme: any
  activeTab: MenopauseActiveTab
  onRefetchConsultationDetail?: (variables?: unknown) => Promise<unknown>
}

const MenopauseViewContent: React.FC<MenopauseViewContentProps> = ({
  consultation,
  colors,
  theme,
  activeTab,
  onRefetchConsultationDetail,
}) => {
  const {t} = useTranslation()
  const history = useHistory()
  const {path} = useRouteMatch()
  const [consultationTypeFilter, setConsultationTypeFilter] =
    React.useState<ConsultationTypeFilter>('all')

  const {hasUpcoming, hasPast} = useMenopauseAppointmentTabPresence()

  const showTabs = hasUpcoming || hasPast

  const [activeCancelUrl, setActiveCancelUrl] = React.useState<string | null>(
    null
  )

  const handleCancelBack = React.useCallback(() => {
    setActiveCancelUrl(null)
    if (onRefetchConsultationDetail) {
      onRefetchConsultationDetail()
    }
  }, [onRefetchConsultationDetail])

  React.useEffect(() => {
    if (activeTab !== 'overview' && activeCancelUrl) {
      setActiveCancelUrl(null)
    }
  }, [activeTab, activeCancelUrl])

  const navigationItems = [
    {
      itemID: 'overview',
      label: t('consultations_program_session_plan_consultation_overview'),
    },
    ...(hasUpcoming
      ? [
          {
            itemID: 'upcoming',
            label: t(
              'consultations_program_session_plan_upcoming_consultations'
            ),
          },
        ]
      : []),
    ...(hasPast
      ? [
          {
            itemID: 'past',
            label: t('consultations_program_session_plan_past_consultations'),
          },
        ]
      : []),
  ]

  const handleNavItemClick = (itemID: string) => {
    if (itemID === 'overview') {
      history.push(MENOPAUSE_BASE_PATH)
    } else if (itemID === 'upcoming') {
      history.push(`${MENOPAUSE_BASE_PATH}/upcoming`)
    } else if (itemID === 'past') {
      history.push(`${MENOPAUSE_BASE_PATH}/past`)
    }
  }

  const activeNavItem = path.endsWith('/past')
    ? 'past'
    : path.endsWith('/upcoming')
      ? 'upcoming'
      : 'overview'

  const appointmentsTabFallback = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 200,
        width: '100%',
      }}
    >
      <CircularProgress color='primary' size={32} />
    </Box>
  )

  return (
    <>
      {showTabs && (
        <ConsultationHubNavigationRow
          items={navigationItems}
          activeItemID={activeNavItem}
          onItemClick={handleNavItemClick}
          colors={colors}
          endAdornment={
            activeTab === 'upcoming' || activeTab === 'past' ? (
              <ConsultationTypeFilterDropdown
                value={consultationTypeFilter}
                onChange={setConsultationTypeFilter}
              />
            ) : null
          }
        />
      )}

      {activeTab === 'overview' && (
        <>
          <Grid item xs={12}>
            {activeCancelUrl ? (
              <CancelConsultation
                cancelUrl={activeCancelUrl}
                onBack={handleCancelBack}
              />
            ) : (
              <OnboardingSection
                consultation={consultation}
                colors={colors}
                alwaysShowBookingOption
                onCancelRequested={(url) => setActiveCancelUrl(url)}
              />
            )}
          </Grid>
          {!activeCancelUrl && (
            <Grid item xs={12}>
              <Box mt={theme.spacing(10)} mb={10} sx={{width: '100%'}}>
                <MenopauseProgramBlock colors={colors} />
              </Box>
            </Grid>
          )}
        </>
      )}

      {activeTab === 'upcoming' && (
        <Suspense fallback={appointmentsTabFallback}>
          <MenopauseAppointmentsLazyPanel
            status='upcoming'
            colors={colors}
            consultationTypeFilter={consultationTypeFilter}
          />
        </Suspense>
      )}

      {activeTab === 'past' && (
        <Suspense fallback={appointmentsTabFallback}>
          <MenopauseAppointmentsLazyPanel
            status='completed'
            colors={colors}
            consultationTypeFilter={consultationTypeFilter}
          />
        </Suspense>
      )}
    </>
  )
}

export default MenopauseViewContent
